import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Box,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { db } from '../firebase';
import { collection, getDocs, doc, deleteDoc, setDoc, query, orderBy, where } from 'firebase/firestore';
import { generateContractProjectKey } from '../userRoles';
import { fetchProjectsAndProducts } from '../services/projectsApi';

const AdminApprovers = () => {
  const [approvers, setApprovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approverToDelete, setApproverToDelete] = useState(null);
  const [actionStatus, setActionStatus] = useState({ success: false, error: null, message: '' });
  
  // Estados para filtros
  const [filterContract, setFilterContract] = useState(null);
  const [filterProject, setFilterProject] = useState('');
  const [filterApprover, setFilterApprover] = useState('');
  const [filterAvailableProducts, setFilterAvailableProducts] = useState([]);
  
  // Estado para proyectos y productos
  const [projects, setProjects] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  
  // Estado para el formulario de agregar/editar aprobador
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingApprover, setEditingApprover] = useState(null);
  const [approverForm, setApproverForm] = useState({
    contractName: '',
    projectName: '',
    email: ''
  });

  // Cargar todos los aprobadores y proyectos al iniciar el componente
  useEffect(() => {
    fetchApprovers();
    fetchProjectsData();
  }, []);

  const fetchProjectsData = async () => {
    try {
      const projectsData = await fetchProjectsAndProducts();
      
      // Procesar los datos para convertir el objeto Product en un array products
      const processedProjects = projectsData.map((project) => {
        // Extraer productos del objeto Product (product_0, product_1, etc.)
        const products = [];
        if (project.Product) {
          Object.values(project.Product).forEach((product) => {
            if (product) products.push(product);
          });
        }
        return {
          id: project.id,
          name: project.name,
          products: products
        };
      });
      
      setProjects(processedProjects);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const fetchApprovers = async () => {
    try {
      setLoading(true);
      const approversRef = collection(db, 'project_emails');
      // No podemos ordenar por contractProjectKey si no está indexado, así que ordenamos en cliente
      const querySnapshot = await getDocs(approversRef);
      
      const approversData = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Solo mostrar los que tienen contractProjectKey (formato nuevo) o projectName (formato viejo)
        // Preferimos mostrar el formato nuevo Contract|Project
        if (data.contractProjectKey || data.projectName) {
            approversData.push({
                id: doc.id,
                ...data
            });
        }
      });
      
      // Ordenar por clave
      approversData.sort((a, b) => {
        const keyA = a.contractProjectKey || a.projectName || '';
        const keyB = b.contractProjectKey || b.projectName || '';
        return keyA.localeCompare(keyB);
      });

      setApprovers(approversData);
      setError(null);
    } catch (error) {
      console.error('Error al cargar aprobadores:', error);
      setError('Error al cargar los aprobadores. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar aprobadores según filtros
  const filteredApprovers = approvers.filter(approver => {
    // Parsear clave para obtener contrato y proyecto
    const key = approver.contractProjectKey || approver.projectName || '';
    let contract = '';
    let project = '';
    
    if (key.includes('|')) {
      [contract, project] = key.split('|');
    } else {
      contract = key;
      project = 'sin_proyecto';
    }

    const matchContract = filterContract ? contract === filterContract : true;
    const matchProject = filterProject ? project.toLowerCase().includes(filterProject.toLowerCase()) : true;
    const matchApprover = filterApprover ? approver.email.toLowerCase().includes(filterApprover.toLowerCase()) : true;

    return matchContract && matchProject && matchApprover;
  });

  // Manejar cambio en filtro de contrato
  const handleFilterContractChange = (event, newValue) => {
    setFilterContract(newValue);
    setFilterProject(''); // Resetear proyecto al cambiar contrato
    
    if (newValue) {
      const selectedProjectData = projects.find(p => p.name === newValue);
      setFilterAvailableProducts(selectedProjectData ? (selectedProjectData.products || []) : []);
    } else {
      setFilterAvailableProducts([]);
    }
  };

  // Limpiar todos los filtros
  const clearFilters = () => {
    setFilterContract(null);
    setFilterProject('');
    setFilterApprover('');
    setFilterAvailableProducts([]);
  };

  // Abrir diálogo de confirmación para eliminar
  const handleDeleteClick = (approver) => {
    setApproverToDelete(approver);
    setDeleteDialogOpen(true);
  };

  // Cerrar diálogo de confirmación
  const handleCloseDialog = () => {
    setDeleteDialogOpen(false);
    setApproverToDelete(null);
  };

  // Eliminar aprobador
  const handleDeleteConfirm = async () => {
    if (!approverToDelete) return;
    
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'project_emails', approverToDelete.id));
      
      setApprovers(prev => prev.filter(a => a.id !== approverToDelete.id));
      
      setActionStatus({
        success: true,
        error: null,
        message: 'Aprobador eliminado correctamente'
      });
      
      setTimeout(() => {
        setActionStatus({ success: false, error: null, message: '' });
      }, 3000);
    } catch (error) {
      console.error('Error al eliminar aprobador:', error);
      setActionStatus({
        success: false,
        error: 'Error al eliminar el aprobador',
        message: ''
      });
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setApproverToDelete(null);
    }
  };

  // Abrir diálogo de formulario (nuevo o editar)
  const handleOpenFormDialog = (approver = null) => {
    if (approver) {
      // Editar
      setEditingApprover(approver);
      
      // Intentar separar Contrato y Proyecto si viene en el formato Contrato|Proyecto
      const key = approver.contractProjectKey || approver.projectName || '';
      let contract = '';
      let project = '';
      
      if (key.includes('|')) {
        const parts = key.split('|');
        contract = parts[0];
        project = parts[1];
      } else {
        // Si no tiene pipe, asumimos que es nombre de proyecto directo (legacy) o todo es contrato
        contract = key;
        project = 'sin_proyecto'; 
      }
      
      // Actualizar productos disponibles basado en el contrato seleccionado
      const selectedProjectData = projects.find(p => p.name === contract);
      if (selectedProjectData) {
        setAvailableProducts(selectedProjectData.products || []);
      } else {
        setAvailableProducts([]);
      }

      setApproverForm({
        contractName: contract,
        projectName: project,
        email: approver.email || ''
      });
    } else {
      // Nuevo
      setEditingApprover(null);
      setAvailableProducts([]);
      setApproverForm({
        contractName: '',
        projectName: '',
        email: ''
      });
    }
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    setEditingApprover(null);
    setApproverForm({
      contractName: '',
      projectName: '',
      email: ''
    });
    setAvailableProducts([]);
  };

  const handleContractChange = (event, newValue) => {
    // newValue será el nombre del contrato (string) o null
    if (newValue) {
      const selectedProjectData = projects.find(p => p.name === newValue);
      const products = selectedProjectData ? (selectedProjectData.products || []) : [];
      
      setAvailableProducts(products);
      setApproverForm(prev => ({
        ...prev,
        contractName: newValue,
        projectName: '' // Resetear proyecto al cambiar contrato
      }));
    } else {
      setAvailableProducts([]);
      setApproverForm(prev => ({
        ...prev,
        contractName: '',
        projectName: ''
      }));
    }
  };

  const handleProjectChange = (event) => {
    setApproverForm(prev => ({
      ...prev,
      projectName: event.target.value
    }));
  };

  const handleEmailChange = (e) => {
    setApproverForm(prev => ({
      ...prev,
      email: e.target.value
    }));
  };

  const handleFormSubmit = async () => {
    // Validaciones
    if (!approverForm.contractName.trim() || !approverForm.email.trim()) {
      alert('Contrato y Email son campos obligatorios');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(approverForm.email)) {
        alert('Formato de email inválido');
        return;
    }

    try {
      setLoading(true);
      
      const contract = approverForm.contractName.trim();
      const project = approverForm.projectName.trim() || 'sin_proyecto';
      
      // Generar la clave usando la función existente o manualmente
      // Usamos generateContractProjectKey si está importada, sino la lógica manual
      const key = `${contract}|${project}`;
      
      // Generar ID de documento sanitizado
      const docId = key.replace(/[^a-zA-Z0-9|]/g, '_').toLowerCase();

      const approverData = {
        contractProjectKey: key,
        proyectos: [contract], // Guardamos el contrato como "proyecto" principal en el array para compatibilidad
        productos: project !== 'sin_proyecto' ? [project] : [],
        email: approverForm.email.trim(),
        projectName: key,
        updatedAt: new Date()
      };

      if (!editingApprover) {
        approverData.createdAt = new Date();
      }

      // Usamos setDoc con merge para crear o actualizar
      await setDoc(doc(db, 'project_emails', docId), approverData, { merge: true });

      // Actualizar lista local
      await fetchApprovers();

      setActionStatus({
        success: true,
        error: null,
        message: editingApprover ? 'Aprobador actualizado correctamente' : 'Aprobador creado correctamente'
      });
      
      setTimeout(() => {
        setActionStatus({ success: false, error: null, message: '' });
      }, 3000);

      handleCloseFormDialog();
    } catch (error) {
      console.error('Error al guardar aprobador:', error);
      setActionStatus({
        success: false,
        error: 'Error al guardar los datos',
        message: ''
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: '#1e293b' }}>
        Administración de Aprobadores por Contrato
      </Typography>

      {actionStatus.message && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {actionStatus.message}
        </Alert>
      )}
      
      {actionStatus.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {actionStatus.error}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Filtros</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Autocomplete
            options={projects.map((p) => p.name)}
            value={filterContract}
            onChange={handleFilterContractChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filtrar por Contrato"
                variant="outlined"
                size="small"
                placeholder="Seleccione un contrato"
              />
            )}
            sx={{ width: { xs: '100%', sm: 300 } }}
          />

          <FormControl size="small" sx={{ width: { xs: '100%', sm: 300 } }}>
            <InputLabel id="filter-project-label">Filtrar por Proyecto</InputLabel>
            <Select
              labelId="filter-project-label"
              value={filterProject}
              label="Filtrar por Proyecto"
              onChange={(e) => setFilterProject(e.target.value)}
              disabled={!filterContract}
            >
              <MenuItem value="">
                <em>Todos</em>
              </MenuItem>
              <MenuItem value="sin_proyecto">
                <em>Sin proyecto específico</em>
              </MenuItem>
              {filterAvailableProducts.map((product) => (
                <MenuItem key={product} value={product}>
                  {product}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            placeholder="Filtrar por Email..."
            variant="outlined"
            size="small"
            value={filterApprover}
            onChange={(e) => setFilterApprover(e.target.value)}
            sx={{ width: { xs: '100%', sm: 300 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Button 
            variant="outlined" 
            color="secondary" 
            onClick={clearFilters}
            disabled={!filterContract && !filterProject && !filterApprover}
          >
            Limpiar Filtros
          </Button>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenFormDialog()}
          >
            Nuevo Aprobador
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
        <Table sx={{ minWidth: 650 }} aria-label="tabla de aprobadores">
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Contrato | Proyecto (Key)</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Email Aprobador</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Última Actualización</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={30} />
                </TableCell>
              </TableRow>
            ) : filteredApprovers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    No se encontraron aprobadores registrados.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredApprovers.map((approver) => (
                <TableRow
                  key={approver.id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: '#f1f5f9' } }}
                >
                  <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                    {approver.contractProjectKey || approver.projectName}
                  </TableCell>
                  <TableCell>{approver.email}</TableCell>
                  <TableCell>
                    {approver.updatedAt?.toDate 
                      ? approver.updatedAt.toDate().toLocaleDateString() 
                      : (approver.updatedAt ? new Date(approver.updatedAt).toLocaleDateString() : '-')}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton 
                        color="primary" 
                        size="small" 
                        onClick={() => handleOpenFormDialog(approver)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton 
                        color="error" 
                        size="small" 
                        onClick={() => handleDeleteClick(approver)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo de Formulario (Crear/Editar) */}
      <Dialog open={formDialogOpen} onClose={handleCloseFormDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingApprover ? 'Editar Aprobador' : 'Nuevo Aprobador'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Ingrese los detalles del contrato, proyecto y el correo del aprobador responsable.
            La clave se generará automáticamente como "Contrato|Proyecto".
          </DialogContentText>
          
          <Autocomplete
            options={projects.map((p) => p.name)}
            value={approverForm.contractName}
            onChange={handleContractChange}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label="Nombre del Contrato"
                fullWidth
                variant="outlined"
                placeholder="Seleccione o busque un contrato"
              />
            )}
            sx={{ mb: 2 }}
          />
          
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel id="project-select-label">Nombre del Proyecto (Producto)</InputLabel>
            <Select
              labelId="project-select-label"
              value={approverForm.projectName}
              label="Nombre del Proyecto (Producto)"
              onChange={handleProjectChange}
            >
              <MenuItem value="sin_proyecto">
                <em>Sin proyecto específico (Todo el contrato)</em>
              </MenuItem>
              {availableProducts.map((product) => (
                <MenuItem key={product} value={product}>
                  {product}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            margin="dense"
            name="email"
            label="Email del Aprobador"
            type="email"
            fullWidth
            variant="outlined"
            value={approverForm.email}
            onChange={handleEmailChange}
            placeholder="aprobador@empresa.com"
          />
          
          {editingApprover && (
            <Box sx={{ mt: 2, p: 1, bgcolor: '#fff3cd', borderRadius: 1 }}>
              <Typography variant="caption" color="warning.dark">
                Nota: Al editar, se creará/actualizará el registro basado en la nueva clave (Contrato|Proyecto). 
                Si cambia el nombre del contrato o proyecto, se creará un nuevo registro.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFormDialog} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleFormSubmit} color="primary" variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Confirmación de Eliminación */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDialog}
      >
        <DialogTitle>¿Eliminar aprobador?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro que desea eliminar la asignación de aprobador para:
            <br />
            <strong>{approverToDelete?.contractProjectKey || approverToDelete?.projectName}</strong>?
            <br /><br />
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminApprovers;
